import { ref, computed, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import FunnelAPI from 'dashboard/api/funnels';
import { useAdmin } from 'dashboard/composables/useAdmin';
import {
  FUNNEL_TEMPLATES,
  createFunnelFromTemplate,
} from 'dashboard/constants/funnelTemplates';

// Funções auxiliares para localStorage
const STORAGE_KEY = 'chatwoot_local_funnels';

const loadLocalFunnels = accountId => {
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY}_${accountId}`);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed;
    }
  } catch (error) {}
  return null;
};

const saveLocalFunnels = (accountId, funnelsData) => {
  try {
    localStorage.setItem(
      `${STORAGE_KEY}_${accountId}`,
      JSON.stringify(funnelsData)
    );
  } catch (error) {}
};

const updateLocalFunnel = (accountId, funnelId, updates) => {
  try {
    const funnels = loadLocalFunnels(accountId) || [];
    const index = funnels.findIndex(f => f.id === funnelId);

    if (index !== -1) {
      // Fazer merge profundo das atualizações
      funnels[index] = {
        ...funnels[index],
        ...updates,
        settings: {
          ...funnels[index].settings,
          ...(updates.settings || {}),
        },
      };

      saveLocalFunnels(accountId, funnels);
      return funnels[index];
    }
  } catch (error) {}
  return null;
};

export function useFunnelManager() {
  const store = useStore();
  const { isAdmin } = useAdmin();

  // State
  const funnels = ref([]);
  const selectedFunnelId = ref(null);
  const isLoading = ref(false);
  const error = ref(null);

  // Computed
  const currentUser = computed(() => store.getters.getCurrentUser);
  const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');

  const currentFunnel = computed(() => {
    if (!selectedFunnelId.value) return null;

    // Buscar funil comparando IDs de forma flexível
    return funnels.value.find(f => {
      // Comparar tanto como string quanto como número
      return (
        f.id == selectedFunnelId.value ||
        f.id === selectedFunnelId.value ||
        parseInt(f.id) === parseInt(selectedFunnelId.value)
      );
    });
  });

  const currentAccount = computed(() => store.getters.getCurrentAccount);
  const currentAccountId = computed(() => store.getters.getCurrentAccountId);

  // Filtrar funis baseado em permissões
  const filterFunnelsByPermission = allFunnels => {
    // Super admin e admin veem todos os funis
    if (isSuperAdmin.value || isAdmin.value) {
      return allFunnels;
    }

    // Agentes veem apenas funis onde estão cadastrados
    const currentUserId = currentUser.value?.id;
    if (!currentUserId) return [];

    const filteredFunnels = allFunnels.filter(funnel => {
      // Verificar se o agente está na lista de agentes do funil
      const agentIds = funnel.settings?.agent_ids || [];
      const isAssigned =
        agentIds.includes(currentUserId) ||
        agentIds.includes(String(currentUserId));

      return isAssigned;
    });

    return filteredFunnels;
  };

  // Methods
  const loadFunnels = async () => {
    const accountId = currentAccountId.value || currentAccount.value?.id;

    if (!accountId) {
      return;
    }

    try {
      isLoading.value = true;
      error.value = null;

      // Tentar buscar via API ou usar store
      let allFunnels = [];
      try {
        await store.dispatch('funnels/get');
        allFunnels = store.getters['funnels/getFunnels'] || [];
      } catch (apiError) {
        allFunnels = [];
      }

      // Aplicar filtro de permissão
      funnels.value = filterFunnelsByPermission(allFunnels);

      // Se não há funis, criar padrões localmente para teste
      if (funnels.value.length === 0) {
        // Tentar carregar funis salvos localmente
        const savedFunnels = loadLocalFunnels(accountId);

        if (savedFunnels && savedFunnels.length > 0) {
          funnels.value = filterFunnelsByPermission(savedFunnels);
        } else {
          // Não criar funis padrão automaticamente
          funnels.value = [];
          // Não salvar funis vazios no localStorage
        }
      }

      // Se não há funil selecionado e existem funis, selecione o primeiro
      if (!selectedFunnelId.value && funnels.value.length > 0) {
        selectedFunnelId.value = funnels.value[0].id;

        // Disparar evento de mudança de funil
        nextTick(() => {
          window.dispatchEvent(
            new CustomEvent('funnel-changed', {
              detail: {
                oldFunnelId: null,
                newFunnelId: funnels.value[0].id,
                funnel: funnels.value[0],
              },
            })
          );
        });
      }
    } catch (err) {
      error.value = 'Erro ao carregar funis';
      funnels.value = [];
    } finally {
      isLoading.value = false;
    }
  };

  const createFunnel = async funnelData => {
    try {
      isLoading.value = true;
      error.value = null;

      const accountId = currentAccountId.value || currentAccount.value?.id;

      // Para funis locais (não temos backend real)
      if (!FunnelAPI || !FunnelAPI.create) {
        // Gerar ID único para o novo funil
        const newFunnelId = `default-${Date.now()}`;
        const newFunnel = {
          id: newFunnelId,
          ...(funnelData.funnel || funnelData),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        // Garantir que não tem inbox_ids
        if (newFunnel.settings) {
          delete newFunnel.settings.inbox_ids;
        }

        // Carregar funis existentes do localStorage
        const existingFunnels = loadLocalFunnels(accountId) || [];

        // Adicionar o novo funil aos existentes
        const updatedFunnels = [...existingFunnels, newFunnel];

        // Salvar todos os funis no localStorage
        saveLocalFunnels(accountId, updatedFunnels);

        // Atualizar o estado local
        funnels.value = updatedFunnels;
        selectedFunnelId.value = newFunnelId;

        return newFunnel;
      }

      // Se tiver API real, usar ela
      const response = await FunnelAPI.create(funnelData);
      const newFunnel = response.data;

      funnels.value.push(newFunnel);
      selectedFunnelId.value = newFunnel.id;

      return newFunnel;
    } catch (err) {
      error.value = 'Erro ao criar funil';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const updateFunnel = async (funnelId, funnelData) => {
    try {
      isLoading.value = true;
      error.value = null;

      // Se for funil local, salvar no localStorage
      if (typeof funnelId === 'string' && funnelId.startsWith('default-')) {
        const accountId = currentAccountId.value || currentAccount.value?.id;
        const updatedFunnel = updateLocalFunnel(
          accountId,
          funnelId,
          funnelData
        );

        if (updatedFunnel) {
          const index = funnels.value.findIndex(f => f.id === funnelId);
          if (index !== -1) {
            funnels.value[index] = updatedFunnel;
          }

          return updatedFunnel;
        }
        throw new Error('Erro ao atualizar funil local');
      }

      // Para funis do backend
      const response = await FunnelAPI.update(funnelId, funnelData);
      const updatedFunnel = response.data;

      const index = funnels.value.findIndex(f => f.id === funnelId);
      if (index !== -1) {
        funnels.value[index] = updatedFunnel;
      }

      return updatedFunnel;
    } catch (err) {
      error.value = 'Erro ao atualizar funil';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const deleteFunnel = async funnelId => {
    try {
      isLoading.value = true;
      error.value = null;

      const accountId = currentAccountId.value || currentAccount.value?.id;

      // Para funis locais
      if (typeof funnelId === 'string' && funnelId.startsWith('default-')) {
        // Carregar funis existentes
        const existingFunnels = loadLocalFunnels(accountId) || [];

        // Filtrar removendo o funil deletado
        const updatedFunnels = existingFunnels.filter(f => f.id !== funnelId);

        // Salvar funis atualizados
        saveLocalFunnels(accountId, updatedFunnels);

        // Atualizar estado local
        funnels.value = updatedFunnels;

        // Se o funil deletado era o selecionado, selecione outro
        if (selectedFunnelId.value === funnelId) {
          selectedFunnelId.value =
            updatedFunnels.length > 0 ? updatedFunnels[0].id : null;
        }

        return;
      }

      // Para funis do backend
      await FunnelAPI.delete(funnelId);

      funnels.value = funnels.value.filter(f => f.id !== funnelId);

      // Se o funil deletado era o selecionado, selecione outro
      if (selectedFunnelId.value === funnelId) {
        selectedFunnelId.value =
          funnels.value.length > 0 ? funnels.value[0].id : null;
      }
    } catch (err) {
      error.value = 'Erro ao deletar funil';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const selectFunnel = async funnelId => {
    try {
      // Se for um funil default/local, não tentar buscar no backend
      if (typeof funnelId === 'string' && funnelId.startsWith('default-')) {
        const funnel = funnels.value.find(f => f.id === funnelId);
        if (funnel) {
          const oldFunnelId = selectedFunnelId.value;
          selectedFunnelId.value = funnelId;

          nextTick(() => {
            window.dispatchEvent(
              new CustomEvent('funnel-changed', {
                detail: {
                  oldFunnelId,
                  newFunnelId: funnelId,
                  funnel: funnel,
                },
              })
            );
          });
        }
        return;
      }

      // Buscar dados completos do funil no backend
      const response = await FunnelAPI.show(funnelId);
      const funnelWithSettings = response.data;

      // Atualizar funil na lista com dados completos
      const funnelIndex = funnels.value.findIndex(f => f.id == funnelId);
      if (funnelIndex !== -1) {
        funnels.value[funnelIndex] = funnelWithSettings;
      }

      // Usar nextTick para garantir reatividade adequada
      const oldFunnelId = selectedFunnelId.value;
      selectedFunnelId.value = parseInt(funnelId);

      // Usar nextTick para emitir evento após atualização do DOM
      nextTick(() => {
        window.dispatchEvent(
          new CustomEvent('funnel-changed', {
            detail: {
              oldFunnelId,
              newFunnelId: parseInt(funnelId),
              funnel: funnelWithSettings,
            },
          })
        );
      });
    } catch (error) {
      // Fallback para funil local se der erro
      const funnel = funnels.value.find(f => f.id == funnelId);
      if (funnel) {
        const oldFunnelId = selectedFunnelId.value;
        selectedFunnelId.value = funnelId;

        window.dispatchEvent(
          new CustomEvent('funnel-changed', {
            detail: {
              oldFunnelId,
              newFunnelId: funnelId,
              funnel: funnel,
            },
          })
        );
      } else {
      }
    }
  };

  const getFunnelStages = funnelId => {
    const funnel = funnels.value.find(f => f.id === funnelId);
    if (!funnel?.stages) return [];

    return Object.entries(funnel.stages)
      .map(([name, config]) => ({
        name,
        ...config,
      }))
      .sort((a, b) => (a.position || 0) - (b.position || 0));
  };

  const addStageToFunnel = async (funnelId, stageData) => {
    const funnel = funnels.value.find(f => f.id === funnelId);
    if (!funnel) throw new Error('Funil não encontrado');

    const newStages = {
      ...funnel.stages,
      [stageData.name]: {
        label: stageData.label,
        color: stageData.color,
        position: stageData.position || Object.keys(funnel.stages).length,
        description: stageData.description,
        sla_hours: stageData.sla_hours,
        allow_new_conversations: stageData.allow_new_conversations || false,
        auto_assign: stageData.auto_assign || false,
      },
    };

    await updateFunnel(funnelId, { stages: newStages });
  };

  const removeStageFromFunnel = async (funnelId, stageName) => {
    const funnel = funnels.value.find(f => f.id === funnelId);
    if (!funnel) throw new Error('Funil não encontrado');

    const newStages = { ...funnel.stages };
    delete newStages[stageName];

    await updateFunnel(funnelId, { stages: newStages });
  };

  const updateStageInFunnel = async (funnelId, stageName, stageData) => {
    const funnel = funnels.value.find(f => f.id === funnelId);
    if (!funnel) throw new Error('Funil não encontrado');

    const newStages = {
      ...funnel.stages,
      [stageName]: {
        ...funnel.stages[stageName],
        ...stageData,
      },
    };

    await updateFunnel(funnelId, { stages: newStages });
  };

  // Função para criar funil a partir de template
  const createFunnelFromTemplateId = async (
    templateId,
    customName = null,
    customSettings = {}
  ) => {
    try {
      isLoading.value = true;
      error.value = null;

      const template = FUNNEL_TEMPLATES.find(t => t.id === templateId);
      if (!template) {
        throw new Error(`Template ${templateId} não encontrado`);
      }

      // Criar funil baseado no template
      const newFunnel = createFunnelFromTemplate(templateId, customSettings);

      // Customizar nome se fornecido
      if (customName) {
        newFunnel.name = customName;
      }

      // Usar o método createFunnel existente para salvar
      const result = await createFunnel(newFunnel);

      return result;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Função para obter lista de templates disponíveis
  const getTemplates = () => {
    return FUNNEL_TEMPLATES.map(template => ({
      id: template.id,
      name: template.name,
      icon: template.settings.icon,
      description: template.description,
      stagesCount: Object.keys(template.stages).length,
      stages: Object.values(template.stages).map(s => s.label),
    }));
  };

  // Função para aplicar template a funil existente
  const applyTemplateToFunnel = async (funnelId, templateId) => {
    try {
      const template = FUNNEL_TEMPLATES.find(t => t.id === templateId);
      if (!template) {
        throw new Error(`Template ${templateId} não encontrado`);
      }

      const funnel = funnels.value.find(f => f.id === funnelId);
      if (!funnel) {
        throw new Error(`Funil ${funnelId} não encontrado`);
      }

      // Atualizar apenas os stages, mantendo outras configurações
      await updateFunnel(funnelId, {
        stages: template.stages,
        funnel_type: template.funnel_type,
      });
    } catch (err) {
      error.value = err.message;
      throw err;
    }
  };

  // Watchers
  watch(
    () => currentAccountId.value || currentAccount.value?.id,
    newAccountId => {
      if (newAccountId) {
        loadFunnels();
      } else {
        funnels.value = [];
        selectedFunnelId.value = null;
      }
    },
    { immediate: true }
  );

  return {
    // State
    funnels,
    selectedFunnelId,
    isLoading,
    error,

    // Computed
    currentFunnel,

    // Methods
    loadFunnels,
    createFunnel,
    updateFunnel,
    deleteFunnel,
    selectFunnel,
    getFunnelStages,
    addStageToFunnel,
    removeStageFromFunnel,
    updateStageInFunnel,

    // Template methods
    createFunnelFromTemplateId,
    getTemplates,
    applyTemplateToFunnel,
  };
}
